import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import TableEducationLevel from '@main/components/TableEducationLevel.vue'

export default {
  components: { TableEducationLevel },
  computed: {
    ...mapState('contestGroup', [
      'generated_at',
      'campaign',
      'contest_group',
      'badges',
      'exclusiveFilter',
      'activeFilters'
    ]),
    flatHeaders() {
      const getLeaves = (header) => {
        if (!header.items || !header.items.length) return [header]
        return header.items.flatMap(getLeaves)
      }
      return this.tableHeaders.flatMap(getLeaves)
    },
    tableHeaders() {
      return this.generateTableHeaders()
    },
    activeBadge() {
      return this.badges.find(item => item.value === this.$route.params.contest_group_id)
    },
    filteredAbiturients() {
      if (!this.contest_group?.abiturients) return []
      let abiturients = [...this.contest_group.abiturients]
      if (this.exclusiveFilter) {
        switch (this.exclusiveFilter) {
          case 'priority':
            return abiturients.filter(a => a.priority === 1 && (a.has_agreement || a.has_contract))
          case 'is_highest_priority':
            return abiturients.filter(a => a.is_highest_priority)
          case 'is_highest_passing_priority':
            return abiturients.filter(a => a.is_highest_passing_priority)
        }
      }

      if (this.activeFilters.includes('has_agreement')) {
        abiturients = abiturients.filter(abiturient => abiturient.has_agreement)
      }
      if (this.activeFilters.includes('priority')) {
        abiturients = abiturients.filter(abiturient => abiturient.priority === 1)
      }
      if (this.activeFilters.includes('has_contract')) {
        abiturients = abiturients.filter(abiturient => abiturient.has_contract)
      }
      return abiturients
    },
  },
  methods: {
    ...mapActions('contestGroup', ['getContestGroup']),
    ...mapMutations('contestGroup', ['CHANGE_EXCLUSIVE_FILTER', 'CHANGE_ACTIVE_FILTERS']),
    generateTableHeaders() {
      const headers = [
        { title: '№', width: '4%', value: 'index' },
        { title: 'Уникальный код', value: 'sspvo_unique_code', width: '8%' },
        {
          title: 'Вступительные испытания',
          items: this.generateEntranceTestsHeaders()
        },
        {
          title: 'Индивидуальные достижения',
          items: this.generateIndividualAchievementsHeaders()
        },
        { title: 'Конкурсный балл', value: 'rating', width: '8%' },
        { title: 'Приоритет', value: 'priority', width: '7%', hint: 'Порядковый номер для этого направления подготовки / специальности в Вашем заявлении' },
        { title: 'Статус', value: 'status', width: '6%' }
      ];

      if (this.isPublished()) {
        headers.splice(headers.length - 1, 0, { title: 'Основной высший приоритет', value: 'is_highest_priority', width: '6%', hint: this.hintForHighestPriority() })
        headers.splice(headers.length - 1, 0, { title: 'Высший проходной приоритет', value: 'is_highest_passing_priority', width: '6%', hint: this.hintForHighestPassingPriority() })
      }
      if (this.isBudget()) {
        headers.splice(headers.length - 1, 0, { title: 'Согласие', value: 'has_agreement', width: '7%' })
      } else {
        headers.splice(headers.length - 1, 0, { title: 'Договор', value: 'has_contract', width: '6%' })
      }

      return headers;
    },
    generateEntranceTestsHeaders() {
      const entranceTestsItems = []
      this.contest_group.entrance_tests.forEach(test => {
        if (test.priority >= 1 && test.priority <= 3) {
          entranceTestsItems.push({
            title: `ВИ ${test.priority}`,
            value: `exam_mark_${test.priority}`,
            width: '4%',
            hint: test.tests.join(' / ')
          })
        }
      })
      entranceTestsItems.push({
        title: 'Сумма баллов',
        value: 'exam_marks_sum',
        width: '5%'
      })
      return entranceTestsItems
    },
    generateIndividualAchievementsHeaders() {
      const items = []
      if (this.contest_group.enrolment_reason === 'Целевой прием') {
        items.push(
          { title: 'Общие', value: 'individual_achievements_mark_common', width: '6%' },
          { title: 'Целевые', value: 'individual_achievements_mark_target', width: '7%' }
        )
      }
      items.push({ title: 'Всего', value: 'individual_achievements_marks_sum', width: '5%' })

      return items
    },
    isExamRelatedField(value) {
      // Проверяем, относится ли поле к экзаменам или индивидуальным достижениям
      return value.startsWith('exam_mark') || value.startsWith('individual_achievements_mark')
    },
    hintForHighestPriority() {
      if (this.isBudget()) {
        return 'Вы проходите по данному конкурсу, но не будете зачислены без согласия на зачисление (да / нет)'
      } else {
        return 'Вы проходите по данному конкурсу, но не будете зачислены без заключенного договора об образовании (да / нет)'
      }
    },
    hintForHighestPassingPriority() {
      if (this.isBudget()) {
        return 'Вы проходите по данному конкурсу и у Вас подано согласие на зачисление (да / нет)'
      } else {
        return 'Вы проходите по данному конкурсу и у Вас заключен договор об образовании (да / нет)'
      }
    },
    hintForExclusiveFilter(filter) {
      const reasonDetails = this.isBudget()
        ? 'согласие на зачисление'
        : 'договор об образовании'
      switch (filter) {
        case 'is_highest_passing_priority':
          return `Список по высшему проходному приоритету (учитывает ${reasonDetails})`
        case 'is_highest_priority':
          return `Список по основному высшему приоритету (не учитывает ${reasonDetails})`
        case 'priority':
          return `Список по первому приоритету (учитывает ${reasonDetails})`
      }
    },
    isMainPlaces() {
      return this.activeBadge && this.activeBadge.text.includes('Основные места')
    },
    isBachelor() {
      return this.$route.params.education_level === 'bachelor'
    },
    isMagistrant() {
      return this.$route.params.education_level === 'magistrant'
    },
    isAspirant() {
      return this.$route.params.education_level === 'aspirant'
    },
    isBudget() {
      return this.activeBadge && !this.activeBadge.text.includes('Платные места')
    },
    isPay() {
      return this.activeBadge && this.activeBadge.text.includes('Платные места')
    },
    isPublished() {
      return false
    },
    showMainPlacesBanner() {
      return false && this.isBachelor() && this.isMainPlaces()
    }
  }
}